import { format, startOfWeek } from "date-fns"
import { DateTime } from "luxon"

export type TExercise = {
  exerciseId?: string
  name: string
  instructions?: string
  isPublic?: boolean
  sets: Array<TExerciseSet>
  rpeMin?: number | null
  rpeMax?: number | null
  maxWeightLifted?: {
    maxWeight: number | null
    reps: number | null
  }
  expanded?: boolean
}

export type TExerciseSet = {
  setId: string
  weight: number
  reps: number
  order: number
  isCompleted: boolean
}

export type TWorkout = {
  workoutId: string
  name: string
  dateScheduled: string
  exercises: Array<TExercise>
  notes?: string
  dateStarted?: string
  dateCompleted?: string
  color?: string
  workoutTemplateId?: string
}

export type TWorkoutTemplate = {
  workoutTemplateId: string
  name: string
  jsonBlob: any
  notes?: string
  color?: string
  dateScheduled?: string
  lastDateCompleted?: string
  averageDuration?: number | null
  folderId?: string | null
  folderName?: string | null
}

export type TFolder = {
  folderId: string
  name: string
  dateCreated: string
}

export type TWorkoutProgram = {
  programId: string
  name: string
  workouts: Array<TWorkout>
  notes?: string
  dateCreated: string
  timesCompleted: number
  durationWeeks: number
  googleSheetsUrl?: string | null
}

export type TProgramStatus = "inactive" | "active" | "completed"

export type TProgramWorkout = {
  programWorkoutId: string
  sortOrder: number
  template: TWorkoutTemplate
}

export type TProgram = {
  programId: string
  userId: string
  name: string
  durationWeeks: number
  notes: string | null
  status: TProgramStatus
  currentWeek: number
  currentWorkoutIndex: number
  dateStarted: string | null
  dateCompleted: string | null
  dateCreated: string
  workoutTemplates: TProgramWorkout[]
}

export type TProgramReportStats = {
  totalWorkouts: number
  completed: number
  skipped: number
  completionRate: number
  weeksCompleted: number
}

export type TProgramWeeklyVolume = {
  week: number
  totalVolume: number
  completedCount: number
}

export type TProgramExerciseProgression = {
  exerciseName: string
  data: { week: number; maxWeight: number }[]
}

export type TProgramTemplateBreakdown = {
  name: string
  completed: number
  skipped: number
  total: number
}

export type TProgramReport = {
  program: {
    programId: string
    name: string
    durationWeeks: number
    notes: string | null
    status: string
    currentWeek: number
    currentWorkoutIndex: number
    dateStarted: string | null
    dateCompleted: string | null
    dateCreated: string
  }
  stats: TProgramReportStats
  weeklyVolume: TProgramWeeklyVolume[]
  exerciseProgression: TProgramExerciseProgression[]
  templateBreakdown: TProgramTemplateBreakdown[]
}

/**
 * This function calculates the total weight lifted across all completed sets of the workout
 */
export const calculateTotalWeight = (workout: TWorkout): number => {
  return workout.exercises.reduce((total, exercise) => {
    const exerciseTotal = exercise.sets.reduce((setTotal, set) => {
      return set.isCompleted ? setTotal + set.weight * set.reps : setTotal
    }, 0)
    return total + exerciseTotal
  }, 0)
}

/**
 * This function calculates the total number of completed reps across all exercises in the workout
 */
export const calculateTotalReps = (workout: TWorkout): number => {
  return workout.exercises.reduce((total, exercise) => {
    const totalReps = exercise.sets.reduce((setTotal, set) => {
      return set.isCompleted ? setTotal + set.reps : setTotal
    }, 0)
    return total + totalReps
  }, 0)
}

/**
 * This function sums the weight lifted across all completed sets of the exercise
 */
export const calculateExerciseWeight = (exercise: TExercise): number => {
  return exercise.sets.reduce((total, set) => {
    return set.isCompleted ? total + set.weight * set.reps : total
  }, 0)
}

/**
 * This function calculates the progress of a specific exercise across all workouts
 * It returns an array of objects with date and weight for each workout containing the exercise
 */
export const calculateExerciseProgress = (
  workouts: Array<TWorkout>,
  exerciseName: string
): Array<{ date: string; weight: number }> => {
  const exerciseData: Array<{ date: string; weight: number; reps: number }> = []

  // Filter workouts to only completed ones with the specific exercise
  const relevantWorkouts = workouts
    .filter((workout) => !!workout.dateCompleted)
    .filter((workout) => workout.exercises.some((ex) => ex.name.toLowerCase() === exerciseName.toLowerCase()))
    .sort((a, b) => DateTime.fromISO(a.dateScheduled).diff(DateTime.fromISO(b.dateScheduled), "days").days)

  relevantWorkouts.forEach((workout) => {
    const exercise = workout.exercises.find((ex) => ex.name.toLowerCase() === exerciseName.toLowerCase())

    if (exercise) {
      const maxWeight = exercise.sets.map((x) => x.weight * x.reps).reduce((acc, set) => acc + set, 0)
      const reps = exercise.sets.reduce((acc, set) => acc + (set.isCompleted ? set.reps : 0), 0)

      exerciseData.push({
        date: DateTime.fromISO(workout.dateScheduled).toLocaleString(DateTime.DATE_MED),
        weight: maxWeight,
        reps: reps,
      })
    }
  })

  return exerciseData
}

/**
 * This function calculates the weekly totals of all completed workouts
 * It returns an array of objects with week start date and total weight lifted
 */
export const getWeeklyTotals = (workouts: Array<TWorkout>): Array<{ week: string; total: number }> => {
  const weeklyTotals: Record<string, number> = {}

  const completedWorkouts = workouts.filter((workout) => workout.dateCompleted)

  completedWorkouts.forEach((workout) => {
    const weekStart = startOfWeek(workout.dateScheduled)
    const weekKey = format(weekStart, "MMM dd, yyyy")
    const totalWeight = calculateTotalWeight(workout)

    if (!weeklyTotals[weekKey]) {
      weeklyTotals[weekKey] = 0
    }
    weeklyTotals[weekKey] += totalWeight
  })

  return Object.entries(weeklyTotals).map(([week, total]) => ({
    week,
    total,
  }))
}

/**
 * This function converts a Workout object to a template format
 */
export const workoutToTemplate = (workout: TWorkout): TWorkoutTemplate => {
  // Deep clone exercises to avoid reference issues
  const clonedExercises = JSON.parse(JSON.stringify(workout.exercises))

  // Reset completed status on all sets
  clonedExercises.forEach((exercise: TExercise) => {
    exercise.sets.forEach((set) => {
      set.isCompleted = false
    })
  })

  return {
    workoutTemplateId: crypto.randomUUID(),
    name: workout.name,
    jsonBlob: { exercises: clonedExercises },
    notes: workout.notes,
    color: "bg-gray-300", // Default color
  }
}

/**
 * This function provides a mapping of template colors to their hex values
 */
export const templateColors = {
  green: "#008000",
  blue: "#0000FF",
  purple: "#800080",
  pink: "#FFC0CB",
  yellow: "#FFFF00",
  orange: "#FFA500",
  red: "#FF0000",
  cyan: "#00FFFF",
  gray: "#808080",
}

// Available template colors
export const templateColorOptions = [
  { name: "Green", value: "bg-green-300" },
  { name: "Blue", value: "bg-blue-300" },
  { name: "Purple", value: "bg-purple-300" },
  { name: "Pink", value: "bg-pink-300" },
  { name: "Yellow", value: "bg-yellow-300" },
  { name: "Orange", value: "bg-orange-300" },
  { name: "Red", value: "bg-red-300" },
  { name: "Cyan", value: "bg-cyan-300" },
  { name: "Gray", value: "bg-gray-300" },
]

/**
 * Checks whether all words in the search input exist in the target string (case-insensitive).
 * Order doesn't matter.
 */
export function wordMatch(input: string, target: string): boolean {
  const inputWords = input.toLowerCase().trim().split(/\s+/)
  const targetLower = target.toLowerCase()

  return inputWords.every((word) => targetLower.includes(word))
}

export type TORMStrategy = "Epley" | "Brzycki" | "Lombardi"
/**
 * Calculate One Rep Max (ORM) using different strategies
 * Supported strategies: Epley, Brzycki, Lombardi
 * Default strategy is Epley
 */
export function calculateORM(
  params: { weight: number; reps: number; strategy?: TORMStrategy } = {
    weight: 0,
    reps: 0,
    strategy: "Epley",
  }
): number {
  if (!params.strategy) {
    params.strategy = "Epley"
  }
  if (params.weight === 0 || params.reps === 0) {
    return 0
  }
  switch (params.strategy) {
    case "Epley":
      return +(params.weight * (1 + params.reps / 30)).toFixed(1)
    case "Brzycki":
      return +(params.weight * (36 / (37 - params.reps))).toFixed(1)
    case "Lombardi":
      return +(params.weight * params.reps ** 0.1).toFixed(1)
    default:
      return params.weight
  }
}

export function displayDuration(dateStarted: string, dateCompleted: string): string {
  const ms = DateTime.fromISO(dateCompleted).diff(DateTime.fromISO(dateStarted), "minutes").toMillis()
  const hoursThreshold = 60
  // choose rounding mode
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number) => String(n).padStart(2, "0")

  if (hours > 0 || minutes >= hoursThreshold) {
    return `${hours}h${pad(minutes)}m${pad(seconds)}s`
  }

  if (minutes < 1) {
    return `${pad(seconds)}s`
  }

  return `${pad(minutes)}m${pad(seconds)}s`
}

export function displayDurationFrom(ms: number): string {
  const hoursThreshold = 60
  // choose rounding mode
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number) => String(n).padStart(2, "0")

  if (hours > 0 || minutes >= hoursThreshold) {
    return `${hours}h${pad(minutes)}m${pad(seconds)}s`
  }

  if (minutes < 1) {
    return `${pad(seconds)}s`
  }

  return `${pad(minutes)}m${pad(seconds)}s`
}
